import { z } from "zod";

/**
 * Written feedback on a review — DM-WF-001 EPIC-063.
 *
 * `sentimentKey` is required here as well as `NOT NULL` in the database. The
 * legacy app's extract claimed its comment column was required and it was an
 * artifact of that platform making every column `NOT NULL`; this one is a real
 * rule, because the sentiment is what HR's reporting groups by and there is no
 * sensible value to backfill onto somebody else's words later.
 */

/** Longer than anyone writes, short enough that a paste-bomb is refused. */
export const FEEDBACK_BODY_MAX = 4000;

/**
 * One note. Creating and editing take the same shape — a note is its body and
 * its tag, and there is nothing about it that only exists at one of the two.
 */
export const reviewFeedbackNoteSchema = z.object({
  body: z.string().trim().min(1, "Write something, or remove the note").max(FEEDBACK_BODY_MAX),
  sentimentKey: z.string().min(1, "Say how this is meant"),
  /**
   * Which question this is about, if any — EPIC-072. Optional: a lot of
   * feedback is general rather than tied to one question on the form.
   */
  criterionId: z.string().uuid().nullable().optional(),
});

/**
 * The manager's release decision.
 *
 * Its own schema rather than a partial of the above: this is the one field a
 * reader rather than the author may change, and keeping it separate means a
 * body can never ride along on a visibility call.
 */
export const reviewFeedbackVisibilitySchema = z.object({
  isVisibleToEmployee: z.boolean(),
});

/**
 * HR's own triage mark — EPIC-072 WI-273. Its own schema for the same reason
 * visibility gets one: this is metadata about the row, not the row's words,
 * and keeping it separate means a body can never ride along on a triage call.
 */
export const reviewFeedbackTriageSchema = z.object({
  status: z.enum(["unreviewed", "reviewed", "needs_follow_up"]),
});

export type ReviewFeedbackNoteInput = z.infer<typeof reviewFeedbackNoteSchema>;
export type ReviewFeedbackVisibilityInput = z.infer<typeof reviewFeedbackVisibilitySchema>;
export type ReviewFeedbackTriageInput = z.infer<typeof reviewFeedbackTriageSchema>;
